import React, { useState } from "react";
import {
  Option,
  DefaultsAdminGrowthRatesFormBedroom,
  DefaultsAdminGrowthRatesFormBedroomType,
  DefaultsAdminGrowthRatesFormPropertyType,
  DefaultsAdminGrowthRatesFormPropertyBedroom,
  DefaultsPageConfiguration,
  KeyDisplayValue,
  DefaultsAdminGrowthRatesFormCountyBedroom
} from "api";
import createDataTable, {
  IPagedTableState,
  ColumnDefinition,
  DefaultInputEditorData,
  DataTableProps
} from "components/controls/createTable";

import Dropdown from "components/controls/Dropdown";
import listUtils from "utils/listUtils";

export type DefaultsGrowthRatesTableRowType =
  | DefaultsAdminGrowthRatesFormBedroom
  | DefaultsAdminGrowthRatesFormPropertyBedroom;

export interface DefaultsGrowthRatesTableProps {
  defaults?: Array<DefaultsGrowthRatesTableRowType> | undefined;
  bedrooms: DefaultsAdminGrowthRatesFormBedroomType[];
  properties?: DefaultsAdminGrowthRatesFormPropertyType[];
  counties?: KeyDisplayValue[];
  onChange: (defaults: Array<DefaultsGrowthRatesTableRowType>) => void;
  hidePropertyColumn?: boolean | undefined;
  hideCountyColumn?: boolean | undefined;
  hideBedroomTypeColumn?: boolean | undefined;
  hideRemoveRowColumn?: boolean | undefined;
  paging?: boolean | undefined;
  onAddRowAction?: (() => void) | undefined;
  pageConfiguration: DefaultsPageConfiguration | undefined;
}

interface TableState extends IPagedTableState {
  divisionFilter?: Option;
  sortField?: any;
  sortOrder?: any;
  sortState?: "unsorted" | "asc" | "desc" | undefined;
}

const DefaultsGrowthRatesTable: React.FC<DefaultsGrowthRatesTableProps> =
  function (props: DefaultsGrowthRatesTableProps) {
    const [table, setTable] = useState<TableState>({
      offset: 0,
      limit: 10,
      sortOrder: 1
    });

    const onRowChanged = (
      oldRow: DefaultsGrowthRatesTableRowType,
      newRow: DefaultsGrowthRatesTableRowType
    ) => {
      if (!props.defaults) {
        return;
      }

      props.onChange(
        listUtils.copyListWithNewItem(props.defaults, oldRow, newRow)
      );
    };

    const onRemoveRow = (row: DefaultsGrowthRatesTableRowType) => {
      if (!props.defaults) {
        return;
      }

      const newDefaults = listUtils.copyListWithItemRemoved(
        props.defaults,
        row
      );
      props.onChange(newDefaults);
    };

    const onGrowthRatesDefaultsFormPropertyChange = (
      defaultsRow: DefaultsGrowthRatesTableRowType
    ) => {
      return (
        selectedProperty: DefaultsAdminGrowthRatesFormPropertyType | undefined
      ) => {
        const newDefaultsRow:
          | DefaultsAdminGrowthRatesFormBedroom
          | DefaultsAdminGrowthRatesFormPropertyBedroom = {
          ...defaultsRow,
          selectedPropertyType: selectedProperty
        };

        onRowChanged(defaultsRow, newDefaultsRow);
      };
    };

    const onGrowthRatesDefaultsFormCountyChange = (
      defaultsRow: DefaultsGrowthRatesTableRowType
    ) => {
      return (selectedCounty: KeyDisplayValue | undefined) => {
        const newDefaultsRow:
          | DefaultsAdminGrowthRatesFormBedroom
          | DefaultsAdminGrowthRatesFormCountyBedroom = {
          ...defaultsRow,
          selectedCountyType: selectedCounty
        };

        onRowChanged(defaultsRow, newDefaultsRow);
      };
    };

    const onGrowthRatesDefaultsFormBedroomChange = (
      defaultsRow: DefaultsGrowthRatesTableRowType
    ) => {
      return (
        selectedBedroom: DefaultsAdminGrowthRatesFormBedroomType | undefined
      ) => {
        const newDefaultsRow:
          | DefaultsAdminGrowthRatesFormBedroom
          | DefaultsAdminGrowthRatesFormPropertyBedroom = {
          ...defaultsRow,
          selectedBedroomType: selectedBedroom
        };

        onRowChanged(defaultsRow, newDefaultsRow);
      };
    };

    const getPropertyDefaultsBody = function (rowData: any, column: any) {
      const defaults = rowData as DefaultsAdminGrowthRatesFormPropertyBedroom;

      let dropDown = (
        <Dropdown
          onChange={onGrowthRatesDefaultsFormPropertyChange(defaults)}
          selectedValue={defaults.selectedPropertyType}
          options={props.properties}
          style={{ minWidth: "100%", maxWidth: "100%" }}
          validationMessage={
            defaults.fieldValidationErrors &&
            defaults.fieldValidationErrors["SelectedPropertyType"]
          }
        />
      );

      let label = (
        <div>
          {defaults.selectedPropertyType &&
            defaults.selectedPropertyType.displayValue}
        </div>
      );

      if (props.pageConfiguration && props.pageConfiguration.isEditEnabled) {
        return dropDown;
      } else {
        return label;
      }
    };

    const getCountyDefaultsBody = function (rowData: any, column: any) {
      const defaults = rowData as DefaultsAdminGrowthRatesFormCountyBedroom;

      let dropDown = (
        <Dropdown
          onChange={onGrowthRatesDefaultsFormCountyChange(defaults)}
          selectedValue={defaults.selectedCountyType}
          options={props.counties}
          style={{ minWidth: "100%", maxWidth: "100%" }}
          validationMessage={
            defaults.fieldValidationErrors &&
            defaults.fieldValidationErrors["SelectedCountyType"]
          }
        />
      );

      let label = (
        <div>
          {defaults.selectedCountyType &&
            defaults.selectedCountyType.displayValue}
        </div>
      );

      if (props.pageConfiguration && props.pageConfiguration.isEditEnabled) {
        return dropDown;
      } else {
        return label;
      }
    };

    const getBedroomDefaultsBody = function (rowData: any, column: any) {
      const defaults = rowData as
        | DefaultsAdminGrowthRatesFormBedroom
        | DefaultsAdminGrowthRatesFormPropertyBedroom;

      let dropDown = (
        <Dropdown
          onChange={onGrowthRatesDefaultsFormBedroomChange(defaults)}
          selectedValue={defaults.selectedBedroomType}
          options={props.bedrooms}
          style={{ minWidth: "100%", maxWidth: "100%" }}
          validationMessage={
            defaults.fieldValidationErrors &&
            defaults.fieldValidationErrors["SelectedBedroomType"]
          }
        />
      );

      let label = (
        <div>
          {defaults.selectedBedroomType &&
            defaults.selectedBedroomType.displayValue}
        </div>
      );

      if (props.pageConfiguration && props.pageConfiguration.isEditEnabled) {
        return dropDown;
      } else {
        return label;
      }
    };

    const getDefaultInputEditorData =
      function (): DefaultInputEditorData<DefaultsGrowthRatesTableRowType> {
        return {
          isEditDisabled: !(
            props.pageConfiguration && props.pageConfiguration.isEditEnabled
          ),
          onChange: (
            row: DefaultsGrowthRatesTableRowType,
            field: keyof DefaultsGrowthRatesTableRowType,
            value: any
          ) => {
            const newDefaultsRow = {
              ...row
            };

            newDefaultsRow[field] = value;
            onRowChanged(row, newDefaultsRow);
          }
        };
      };

    const getColumns = function (): any[] {
      const columns = [] as ColumnDefinition<
        DefaultsGrowthRatesTableRowType,
        keyof DefaultsGrowthRatesTableRowType
      >[];

      const propertyColumnStyle = { width: "45%" };
      const bedroomTypeColumnStyle = { width: "30%" };
      const GrowthRatePercentageColumnStyle = {
        width: "25%",
        textAlign: "center"
      };

      if (!props.hidePropertyColumn) {
        columns.push({
          header: "Property",
          style: propertyColumnStyle,
          body: getPropertyDefaultsBody
        });
      } else if (!props.hideCountyColumn) {
        columns.push({
          header: "County",
          style: propertyColumnStyle,
          body: getCountyDefaultsBody
        });
      } else {
        columns.push({ header: "", style: propertyColumnStyle });
      }

      if (!props.hideBedroomTypeColumn) {
        columns.push({
          header: "Bedroom",
          style: bedroomTypeColumnStyle,
          body: getBedroomDefaultsBody
        });
      } else {
        columns.push({ header: "", style: bedroomTypeColumnStyle });
      }

      return columns.concat([
        {
          field: "growthRatePercentage",
          header: "Growth Rate %",
          sortable: true,
          style: GrowthRatePercentageColumnStyle,
          defaultInputEditorData: getDefaultInputEditorData()
        }
      ]);
    };

    const tableProps: DataTableProps<
      DefaultsGrowthRatesTableRowType,
      TableState
    > = {
      value: props.defaults || undefined,
      editMode: "cell",
      state: table,
      updateState: setTable,
      paging: props.paging,
      // TODO: This functionality only works when added at this leve. I was unable to get it to work when I moved it up to the createDataTable function
      sortField: table.sortField,
      sortOrder: table.sortOrder,
      onSort: (e: any) => {
        //clear sort column
        if (
          table.sortOrder === -1 &&
          e.sortOrder === 1 &&
          table.sortField === e.sortField
        ) {
          setTable({
            ...table,
            sortField: undefined,
            sortOrder: undefined
          });
        } else {
          setTable({
            ...table,
            sortField: e.sortField,
            sortOrder: e.sortOrder
          });
        }
      },
      onAddRowAction: props.onAddRowAction,
      onRemoveRowAction: props.hideRemoveRowColumn ? undefined : onRemoveRow,
      emptyMessage: "No defaults have been added."
    };
    const dataTable = createDataTable<
      DefaultsGrowthRatesTableRowType,
      TableState
    >(getColumns(), tableProps);

    return <div>{dataTable}</div>;
  };

export default DefaultsGrowthRatesTable;
